/** Saved Kanban boards render Rust/WASM projections over the current task store. User paint is restricted to frames; activity actions keep opaque app-owned surfaces.
 * @module apps/web/src/components/kanban/KanbanView
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TaskUI } from '@fluxability/types';
import { taskToUI, numberToPriority } from '@fluxability/types';
import { useRouteTasks } from '@/stores/routeData';
import { Detached, useDetachedState } from '@/hooks/useDetachedState';
import { useTasksStore } from '@/stores/tasksStore';
import { useUserStore } from '@/stores/userStore';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import { TaskBlock } from '@/components/blocks/TaskBlock';
import { TaskEditor } from '@/components/blocks/TaskEditor';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { loadFluxAgentBrowser } from '@/lib/fluxAgentBrowser';
import {
  kanbanApi,
  projectKanban,
  notifyKanbanChanged,
  type KanbanState,
  type KanbanSavedView,
  type KanbanSpec,
  type KanbanPaint,
  type KanbanProjectedColumn,
  type KanbanProjection,
} from '@/lib/kanbanApi';
import { KanbanViewEditor } from './KanbanViewEditor';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  useDroppable,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { cn } from '@/lib/utils';

/** Existing lifecycle targets; labels are app-owned action text, unaffected by custom column titles. */
const statuses = [
  { id: 'todo', title: 'To Do' },
  { id: 'in_progress', title: 'Doing' },
  { id: 'done', title: 'Done' },
] as const;
/** One status mutation accepted by the existing task API. */
type BoardStatus = (typeof statuses)[number]['id'];

/** Owner-keyed board mount prevents a previous account's settings or editing draft from rendering. */
export function KanbanView() {
  const owner = useUserStore(state => state.userId);
  return owner ? <KanbanBoard key={owner} owner={owner} /> : <p>Sign in to open your boards.</p>;
}
/** Canonical Rust column frame; only a status projection creates an active drop target. */
function KanbanColumnPanel({
  column,
  paint,
  titlePaint,
  children,
}: {
  /** Rust-projected group and IDs. */ column: KanbanProjectedColumn;
  /** Closed frame paint. */ paint: KanbanPaint;
  /** Closed title typography. */ titlePaint: KanbanPaint;
  /** Existing task cards. */ children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id, disabled: column.status === null });
  return (
    <section
      ref={setNodeRef}
      aria-label={`${column.title} column`}
      data-kanban-column={column.id}
      className={cn(
        'bg-muted/20 flex h-full w-[300px] shrink-0 flex-col rounded-md border p-2',
        isOver && 'ring-primary ring-2'
      )}
      style={{
        backgroundColor: paint.backgroundColor ?? undefined,
        borderColor: paint.borderColor ?? undefined,
        borderRadius: paint.borderRadius ?? undefined,
        padding: paint.padding ?? undefined,
      }}
    >
      <header className="bg-background text-foreground mb-2 flex items-center justify-between rounded px-2 py-2">
        <h3
          className="text-sm font-semibold"
          style={{
            fontSize: titlePaint.fontSize ?? undefined,
            fontWeight: titlePaint.fontWeight ?? undefined,
          }}
        >
          {column.title}
        </h3>
        <Badge variant="secondary">{column.taskIds.length}</Badge>
      </header>
      <div className="flex min-h-12 flex-1 flex-col gap-2">{children}</div>
    </section>
  );
}
/** Only presentation fields consumed by cards; column/filter changes do not invalidate them. */
interface CardAppearance {
  /** Visible validated metadata. */ fields: KanbanSpec['cardFields'];
  /** Whether compact card spacing is selected. */ compact: boolean;
  /** Whether this grouping supports lifecycle dragging. */ dragDisabled: boolean;
  /** Validated frame-only paint. */ paint: KanbanPaint;
}
/** Memoized existing activity card; painting its frame never targets its controls. */
const KanbanCard = memo(function KanbanCard({
  task,
  appearance,
  onEdit,
  onStatus,
}: {
  /** Existing canonical task-store row. */ task: TaskUI;
  /** Stable card-only presentation. */ appearance: CardAppearance;
  /** Stable editor callback. */ onEdit: (id: string) => void;
  /** Stable lifecycle mutation callback. */ onStatus: (id: string, status: BoardStatus) => void;
}) {
  const { paint } = appearance;
  return (
    <div
      className="bg-background/40 rounded-lg border p-2"
      data-kanban-card={task.id}
      style={{
        backgroundColor: paint.backgroundColor ?? undefined,
        borderColor: paint.borderColor ?? undefined,
        borderRadius: paint.borderRadius ?? undefined,
        padding: paint.padding ?? undefined,
        contentVisibility: 'auto',
        containIntrinsicSize: 'auto 180px',
      }}
    >
      <div className="bg-background text-foreground rounded" data-kanban-protected>
        <TaskBlock
          task={task}
          onEdit={onEdit}
          visibleFields={appearance.fields}
          compact={appearance.compact}
          dragDisabled={appearance.dragDisabled}
        />
        <div className="grid grid-cols-2 gap-1 p-1 sm:grid-cols-4">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={() => onEdit(task.id)}
          >
            Edit
          </Button>
          {statuses.map(status => (
            <Button
              key={status.id}
              type="button"
              size="sm"
              className="h-8 text-xs"
              variant={
                task.status === status.id || (task.status === 'completed' && status.id === 'done')
                  ? 'default'
                  : 'outline'
              }
              onClick={() => onStatus(task.id, status.id)}
            >
              {status.title}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
});
/** Keep board children stable while save controls, dialogs, or acknowledgement notices change. */
const KanbanColumns = memo(function KanbanColumns({
  projection,
  appearance,
  taskById,
  onEdit,
  onStatus,
}: {
  /** Rust-projected current columns and paint. */ projection: KanbanProjection;
  /** Stable presentation independent of task placement. */ appearance: CardAppearance;
  /** Current activity objects indexed by identity. */ taskById: Map<string, TaskUI>;
  /** Stable activity editor callback. */ onEdit: (id: string) => void;
  /** Stable activity status callback. */ onStatus: (id: string, status: BoardStatus) => void;
}) {
  return (
    <div
      className="flex h-full min-w-max gap-3"
      data-kanban-board
      style={{
        backgroundColor: projection.styles.board.backgroundColor ?? undefined,
        padding: projection.styles.board.padding ?? undefined,
        gap: projection.styles.board.gap ?? undefined,
      }}
    >
      {projection.columns.map(column => (
        <KanbanColumnPanel
          key={column.id}
          column={column}
          paint={projection.styles.column}
          titlePaint={projection.styles.columnTitle}
        >
          {column.taskIds.map(id => {
            const task = taskById.get(id);
            return task ? (
              <KanbanCard
                key={id}
                task={task}
                appearance={appearance}
                onEdit={onEdit}
                onStatus={onStatus}
              />
            ) : null;
          })}
        </KanbanColumnPanel>
      ))}
      {projection.columns.length === 0 && (
        <p className="bg-background text-foreground rounded p-4">
          All columns are hidden. Edit this view or reset to default.
        </p>
      )}
    </div>
  );
});
/** Saved view controls and local Rust projection. Network reads occur on invalidation, never on drag/filter projection. */
function KanbanBoard({
  owner,
}: {
  /** Current owner captured for every asynchronous response fence. */ owner: string;
}) {
  const tasks = useRouteTasks();
  const updateTask = useTasksStore(state => state.updateTask);
  const bundles = useWorkspaceStore(state => state.bundles);
  const [state, setState] = useState<KanbanState | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [savedNotice, setSavedNotice] = useState<string | null>(null);
  const [editor, setEditor] = useState<{
    view: KanbanSavedView | null;
    initial: KanbanSpec;
  } | null>(null);
  const [deleteReview, setDeleteReview] = useState<KanbanSavedView | null>(null);
  const activityEditor = useDetachedState<string | null>();
  const setEditingId = activityEditor.set;
  const [activeTask, setActiveTask] = useState<TaskUI | null>(null);
  const generation = useRef(0);
  const [notificationSource] = useState(() => crypto.randomUUID());
  const ownerCurrent = useCallback(() => useUserStore.getState().userId === owner, [owner]);
  const invalidate = useCallback(() => {
    generation.current++;
  }, []);
  const refresh = useCallback(async () => {
    const request = ++generation.current;
    const response = await kanbanApi.list(owner);
    if (!ownerCurrent() || request !== generation.current) return;
    if (response.success && response.data?.ownerId === owner) setState(response.data);
    else setError(response.error?.message ?? 'Could not load saved views.');
  }, [ownerCurrent, owner]);
  useEffect(() => {
    let cancelled = false;
    void loadFluxAgentBrowser()
      .then(() => {
        if (!cancelled) setReady(true);
      })
      .catch(() => {
        if (!cancelled) setError('The board runtime could not load. Reload to retry.');
      });
    void refresh();
    const onRefresh = (event: Event) => {
      if (event instanceof CustomEvent && event.detail === notificationSource) return;
      void refresh();
    };
    window.addEventListener('fluxability-kanban-changed', onRefresh);
    window.addEventListener('focus', onRefresh);
    const channel =
      typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('fluxability-kanban');
    if (channel)
      channel.onmessage = event => {
        if (event.data !== notificationSource) void refresh();
      };
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refresh();
    }, 30_000);
    return () => {
      cancelled = true;
      invalidate();
      window.removeEventListener('fluxability-kanban-changed', onRefresh);
      window.removeEventListener('focus', onRefresh);
      channel?.close();
      window.clearInterval(timer);
    };
  }, [refresh, invalidate, notificationSource]);
  const selected = state?.views.find(view => view.id === state.activeId) ?? null;
  // Refreshes replace transport objects even when their presentation is unchanged.
  // Memoize by exact content so an acknowledgement does not repaint every card.
  const specJson = JSON.stringify(selected?.spec ?? state?.defaultSpec ?? null);
  const spec = useMemo(() => JSON.parse(specJson) as KanbanSpec | null, [specJson]);
  const projected = useMemo(
    () => (ready ? projectKanban(spec, tasks, bundles) : null),
    [ready, spec, tasks, bundles]
  );
  const projection = projected?.ok ? projected.projection : null;
  const appearanceJson = JSON.stringify(
    projection && {
      fields: projection.spec.cardFields,
      compact: projection.spec.density === 'compact',
      dragDisabled: projection.spec.grouping !== 'status',
      paint: projection.styles.card,
    }
  );
  const appearance = useMemo(
    () => JSON.parse(appearanceJson) as CardAppearance | null,
    [appearanceJson]
  );
  const taskById = useMemo(() => new Map(tasks.map(task => [task.id, task])), [tasks]);
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));
  const edit = useCallback((id: string) => setEditingId(id), [setEditingId]);
  const closeEditor = useCallback(() => setEditingId(null), [setEditingId]);
  const changeStatus = useCallback(
    (id: string, status: BoardStatus) => {
      const task = useTasksStore.getState().tasks.find(item => item.id === id);
      if (!task || task.status === status || (task.status === 'completed' && status === 'done'))
        return;
      updateTask(id, { status });
      void api.tasks
        .update(id, { status })
        .then(response => {
          if (!ownerCurrent()) return;
          if (!response.success) {
            updateTask(id, { status: task.status });
            setError(response.error?.message ?? 'Could not change activity status.');
          }
        })
        .catch(() => {
          if (ownerCurrent()) {
            updateTask(id, { status: task.status });
            setError('Could not change activity status.');
          }
        });
    },
    [ownerCurrent, updateTask]
  );
  const dragStart = useCallback(
    (event: DragStartEvent) =>
      setActiveTask(
        useTasksStore.getState().tasks.find(task => task.id === event.active.id) ?? null
      ),
    []
  );
  const dragEnd = (event: DragEndEvent) => {
    setActiveTask(null);
    if (!event.over || !projection || projection.spec.grouping !== 'status') return;
    const target = projection.columns.find(
      column => column.id === event.over?.id || column.taskIds.includes(String(event.over?.id))
    );
    if (target?.status) changeStatus(String(event.active.id), target.status);
  };
  const choose = async (id: string | null) => {
    if (!state) return;
    const view = state.views.find(item => item.id === id);
    setBusy(true);
    setError(null);
    setSavedNotice(null);
    const response = await kanbanApi.select(
      owner,
      id,
      view?.revision ?? null,
      state.selectionRevision
    );
    if (!ownerCurrent()) return;
    setBusy(false);
    if (response.success && response.data?.ownerId === owner) {
      generation.current++;
      setState(response.data);
      notifyKanbanChanged(notificationSource);
    } else {
      setError(response.error?.message ?? 'Could not select view.');
      void refresh();
    }
  };
  const save = async (draft: KanbanSpec) => {
    if (!editor) return;
    setBusy(true);
    setError(null);
    setSavedNotice(null);
    const response = editor.view
      ? await kanbanApi.update(owner, editor.view.id, editor.view.revision, draft)
      : await kanbanApi.create(owner, draft);
    if (!ownerCurrent()) return;
    setBusy(false);
    if (!response.success || !response.data) {
      setError(response.error?.message ?? 'Could not save view.');
      return;
    }
    setSavedNotice(`Saved ${response.data.view.spec.name}.`);
    setEditor(null);
    await refresh();
    notifyKanbanChanged(notificationSource);
  };
  const remove = async () => {
    if (!deleteReview) return;
    setBusy(true);
    setError(null);
    setSavedNotice(null);
    const response = await kanbanApi.delete(owner, deleteReview.id, deleteReview.revision);
    if (!ownerCurrent()) return;
    setBusy(false);
    if (!response.success) {
      setError(response.error?.message ?? 'Could not delete view.');
      return;
    }
    setDeleteReview(null);
    await refresh();
    notifyKanbanChanged(notificationSource);
  };
  return (
    <div className="flex h-full flex-col">
      <div className="border-b pb-4">
        <h2 className="text-2xl font-semibold tracking-normal">Activities board</h2>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-sm">
            Saved view
            <select
              aria-label="Saved view"
              className="bg-background rounded-md border p-2"
              value={state?.activeId ?? ''}
              disabled={!state || busy}
              onChange={event => void choose(event.target.value || null)}
            >
              <option value="">Default board</option>
              {state?.views.map(view => (
                <option key={view.id} value={view.id}>
                  {view.spec.name}
                </option>
              ))}
            </select>
          </label>
          <Button
            size="sm"
            disabled={!ready || !state || busy}
            onClick={() => {
              setError(null);
              setSavedNotice(null);
              if (state)
                setEditor({ view: null, initial: { ...state.defaultSpec, name: 'My board' } });
            }}
          >
            New view
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!ready || !selected || busy}
            onClick={() => {
              setError(null);
              setSavedNotice(null);
              if (selected) setEditor({ view: selected, initial: selected.spec });
            }}
          >
            Edit view
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!selected || busy}
            onClick={() => setDeleteReview(selected)}
          >
            Delete view
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={!selected || busy}
            onClick={() => void choose(null)}
          >
            Reset to default
          </Button>
        </div>
        {projection && (
          <p className="text-muted-foreground mt-2 text-xs">
            {projection.spec.grouping === 'status'
              ? 'Drag a card to change its status, or use its status buttons.'
              : 'Grouped by ' +
                projection.spec.grouping +
                '. Use status buttons to change progress; dragging is unavailable in this grouping.'}{' '}
            {projection.hiddenCount > 0 &&
              `${projection.hiddenCount} activities hidden by this view.`}
          </p>
        )}
        {savedNotice && (
          <p role="status" className="mt-2 text-sm">
            {savedNotice}
          </p>
        )}
        {state && state.invalidCount > 0 && (
          <p role="status" className="mt-2 text-sm">
            Some saved views could not be validated and were not loaded. The default board is safe
            to use.
          </p>
        )}
        {error && !editor && (
          <p role="alert" className="text-destructive mt-2 text-sm">
            {error}
          </p>
        )}
        {projected && !projected.ok && (
          <p role="alert" className="text-destructive mt-2 text-sm">
            {projected.error}
          </p>
        )}
        {deleteReview && (
          <Dialog
            open
            onOpenChange={open => {
              if (!open && !busy) setDeleteReview(null);
            }}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete saved view</DialogTitle>
                <DialogDescription>
                  Delete “{deleteReview.spec.name}”? Your activities will remain.
                </DialogDescription>
              </DialogHeader>
              <div className="flex gap-2">
                <Button disabled={busy} onClick={() => void remove()}>
                  Confirm delete view
                </Button>
                <Button variant="outline" disabled={busy} onClick={() => setDeleteReview(null)}>
                  Cancel
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
      {!projection ? (
        <p className="p-4">Loading board…</p>
      ) : (
        <div className="flex-1 overflow-x-auto py-4">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={dragStart}
            onDragEnd={dragEnd}
            onDragCancel={() => setActiveTask(null)}
          >
            <KanbanColumns
              projection={projection}
              appearance={appearance!}
              taskById={taskById}
              onEdit={edit}
              onStatus={changeStatus}
            />
            <DragOverlay>
              {activeTask ? (
                <div className="bg-background text-foreground rounded p-2">{activeTask.title}</div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      )}
      {editor && state && (
        <KanbanViewEditor
          initial={editor.initial}
          defaults={state.defaultSpec}
          tasks={tasks}
          bundles={bundles}
          busy={busy}
          error={error}
          onSave={draft => void save(draft)}
          onClose={() => {
            setEditor(null);
            setError(null);
          }}
        />
      )}
      <Detached state={activityEditor} initial={null}>
        {editingId => <KanbanTaskEditor id={editingId} onClose={closeEditor} />}
      </Detached>
    </div>
  );
}
/** Detached activity editor subscription keeps typing and session changes from rerendering the whole board. */
function KanbanTaskEditor({
  id,
  onClose,
}: {
  /** Existing activity being edited. */ id: string | null;
  /** Dismiss editor. */ onClose: () => void;
}) {
  const owner = useUserStore(state => state.userId);
  const editingTask = useTasksStore(state => state.tasks.find(task => task.id === id) ?? null);
  const updateTask = useTasksStore(state => state.updateTask);
  const deleteTask = useTasksStore(state => state.deleteTask);
  const handleSaveTask = async (updates: Partial<TaskUI>) => {
    if (!editingTask) return;

    try {
      const input = {
        title: updates.title,
        description: updates.description,
        bundle_id: updates.bundleId,
        due_date: updates.deadline,
        do_date: updates.scheduledStart,
        do_date_end: updates.scheduledEnd,
        duration_minutes: updates.estimatedDuration,
        priority: updates.priority ? numberToPriority(updates.priority) : undefined,
        state_profile: updates.stateProfile,
        split_policy: updates.splitPolicy,
        custom_attributes: updates.customAttributes,
      };
      const response = await api.tasks.update(editingTask.id, input);
      if (useUserStore.getState().userId !== owner) return;
      if (response.success && response.data) {
        updateTask(editingTask.id, taskToUI(response.data?.task || response.data));
      } else {
        updateTask(editingTask.id, updates); // Optimistic fallback
      }
    } catch (err) {
      if (useUserStore.getState().userId !== owner) return;
      console.error('Failed to save activity', err);
      updateTask(editingTask.id, updates); // Optimistic fallback
    }
  };

  const handleDeleteTask = async () => {
    if (!editingTask) return;

    try {
      await api.tasks.delete(editingTask.id);
      if (useUserStore.getState().userId !== owner) return;
      deleteTask(editingTask.id);
      onClose();
    } catch (err) {
      console.error('Failed to delete activity', err);
    }
  };

  return (
    <TaskEditor
      task={editingTask}
      open={!!editingTask}
      onClose={onClose}
      onSave={handleSaveTask}
      onDelete={handleDeleteTask}
    />
  );
}
